import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthProvider";

interface LocationResult {
  lat: number | string;
  lon: number | string;
  city?: string;
  region?: string;
  country?: string;
  address?: string;
  displayName?: string;
  source: "gps" | "ip" | "manual" | "stored";
}

export default function SecureLocationRequest() {
  const { user, setUserLocation } = useAuth();

  const [showPopup, setShowPopup] = useState(false);
  const [manualAddress, setManualAddress] = useState("");
  const [error, setError] = useState<string | null>(null);
   const [loading, setLoading] = useState(false);
  const [isPermanentlyBlocked, setPermanentlyBlocked] = useState(false);

  /**
   * AUTO-DETECT WITH FIX:
   * ✔ Only run GPS if user has NO stored lat/lon/location
   * ✔ Prevents GPS from running on every refresh
   */
  useEffect(() => {
    if (!user) return;

    const hasStoredLocation =
      user.latitude &&
      user.longitude &&
      user.location &&
      user.latitude !== "0.0" &&
      user.longitude !== "0.0";

    // If backend already has location → use stored value & DO NOT call GPS
    if (hasStoredLocation) {
      setUserLocation({
        lat: user.latitude,
        lon: user.longitude,
        displayName: user.location,
        source: "stored",
      });
      return;
    }

    // First-time login or missing location → request GPS
    requestGPS();
  }, [user]);

  /** Detect permanent block via Permissions API */
  const checkPermissionState = async () => {
    try {
      const perm = await navigator.permissions.query({
        name: "geolocation",
      });

      if (perm.state === "denied") {
        setPermanentlyBlocked(true);
        setError("Location is permanently blocked.");
        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  /** MAIN GPS FUNCTION */
  const requestGPS = async () => {
    setLoading(true);
     if (!navigator.geolocation) { 
    setError("Geolocation is not supported by this browser.");
    setLoading(false);
    return;
  }
    const blocked = await checkPermissionState();

    if (blocked) {
      setShowPopup(true);
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition( 
      async (pos) => {
        const result: LocationResult = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          source: "gps",
        };

        setUserLocation(result);
        setShowPopup(false);
        setLoading(false);
      },
      (err: GeolocationPositionError) => {
        if (err.code === GeolocationPositionError.PERMISSION_DENIED) {
          setShowPopup(true);
          setError("Permission denied.");
        }
        setLoading(false);
      },
      { enableHighAccuracy: true }
    );
  };

  /** IP FALLBACK */
  const requestIP = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();

      const result: LocationResult = {
        lat: data.latitude,
        lon: data.longitude,
        city: data.city,
        region: data.region,
        country: data.country_name,
        source: "ip",
      };

      setUserLocation(result);
      setShowPopup(false);
    } catch {
      setError("Failed to fetch IP-based location");
    }
    setLoading(false);
  };

  /** MANUAL ADDRESS FALLBACK */
  const submitManual = async () => {
    if (!manualAddress.trim()) {
      setError("Please enter an address.");
      return;
    }

    setLoading(true);

    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        manualAddress
      )}&format=json&limit=1`;

      const res = await fetch(url, {
        headers: { "User-Agent": "YourAppName" },
      });

      const data = await res.json();

      if (!data.length) {
        setError("Address not found.");
        setLoading(false);
        return;
      }

      const loc = data[0];

      const result: LocationResult = {
        lat: loc.lat,
        lon: loc.lon,
        displayName: loc.display_name,
        source: "manual",
      };

      setUserLocation(result);
      setShowPopup(false);
    } catch {
      setError("Failed to convert address.");
    }

    setLoading(false);
  };

  /** RETRY BUTTON LOGIC */
  const retry = async () => {
    const blocked = await checkPermissionState();

    if (blocked) {
      setError("Location blocked in browser settings.");
      return;
    }

    requestGPS();
  };

  return (
    <>
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white w-96 p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-bold mb-3">Location Required</h2>
            <p className="text-gray-600 mb-4">
              Your location is required for security. Please choose an option.
            </p>

            {error && <p className="text-red-500 mb-2">{error}</p>}

            {!isPermanentlyBlocked && (
              <button
                onClick={retry}
                className="w-full bg-blue-600 text-white py-2 rounded-lg mt-2"
              >
                 {loading ? "Loading..." : "Retry GPS"}
              </button>
            )}

            {isPermanentlyBlocked && (
              <div className="text-left mt-3 text-sm">
                <p className="text-red-500 font-semibold">
                  GPS blocked in browser settings.
                </p>
                <p className="mt-1 text-gray-700">Enable location manually:</p>

                <ul className="list-disc ml-5 mt-2 text-gray-600 text-sm">
                  <li>Browser Settings → Privacy → Location</li>
                  <li>Enable “Allow Location”</li>
                </ul>

                <button
                  onClick={() =>
                    window.open("chrome://settings/content/location")
                  }
                  className="w-full bg-gray-700 text-white py-2 rounded-lg mt-3"
                >
                  Open Chrome Location Settings
                </button>
              </div>
            )}

            {/* OPTIONAL - HIDDEN */}
            <button
              onClick={requestIP}
              className="hidden w-full bg-gray-800 text-white py-2 rounded-lg mt-3"
            >
              Use IP-Based Location
            </button>

            <div className="hidden mt-4 text-left">
              <p className="text-sm font-semibold">Manual Address</p>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 mt-1"
                placeholder="Enter address or city"
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
              />
              <button
                onClick={submitManual}
                className="w-full bg-blue-600 text-white py-2 rounded-lg mt-3"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
